/* istanbul ignore next */
import Constants from '../common/utils/constants';
import Router from 'next/router';
import cookie from 'cookie';
import cookies from 'js-cookie';

import errorHandler from 'common/utils/errorHandler';
import Project from '../common/project';
import { validateCognitoToken, TOKEN_VALIDATION_STATE, refreshTokens } from '../common/utils/jwt';

const API = {
    isMobile: () => false,
    ajaxHandler(type, e) {
        return { type, error: errorHandler(e) };
    },
    logout() {
        cookies.remove('token');
        cookies.remove('evolveyou_id_token');
        cookies.remove('evolveyou_refresh_token');
        cookies.remove('evolveyou_access_token');
        Router.replace(Project.logoutRedirect || '/');
    },
    loggedIn() {
        const params = Utils.fromParam();
        params.redirect = params.redirect || Project.accountPage;
        Router.replace(params.redirect, params.as || params.redirect);
    },
    getStoredToken(req) {
        if (req) {
            const parsedCookies = cookie.parse(req.headers.cookie || '');
            return parsedCookies && parsedCookies.token;
        }
        return cookies.get('evolveyou_id_token');
    },
    getStoredUser(req) {
        if (req) {
            const parsedCookies = cookie.parse(req.headers.cookie || '');
            return parsedCookies && parsedCookies.user;
        }
        return cookies.get('user') && JSON.parse(cookies.get('user'));
    },
    getStoredRefreshToken(req) {
        if (req) {
            const parsedCookies = cookie.parse(req.headers.cookie || '');
            return parsedCookies && parsedCookies.refreshToken;
        }
        const refreshToken = cookies.get('evolveyou_refresh_token');
        return refreshToken && JSON.parse(refreshToken);
    },
    setStoredRefreshToken(v) {
        return API.setCookie('refreshToken', v);
    },
    getStoredLocale(req) {
        if (req) {
            // Attempt to get locale saved cookie
            const parsedCookies = cookie.parse(req.headers.cookie || '');
            if (parsedCookies.locale) {
                return parsedCookies.locale;
            }
            // Attempt to retrieve local from Accept-Language headers
            if (req.headers && req.headers['accept-language']) {
                const parsedLocale = req.headers['accept-language'].split(',')[0];
                if (parsedLocale) {
                    return parsedLocale;
                }
            }
        }

        return Constants.defaultLocale;
    },
    getCookie(name, req) {
        if (req) {
            const parsedCookies = cookie.parse(req.headers.cookie || '');
            return parsedCookies && parsedCookies[name];
        }
        if (typeof window === 'undefined') {
            console.log('Error, attempted to get a cookie without a request');
            return;
        }
        return cookies.get(name);
    },
    setStoredToken(v) {
        return API.setCookie('token', v);
    },
    setCookie(name, value, opts) {
        if (typeof window === 'undefined') {
            return;
        }
        return cookies.set(name, value, opts);
    },
    trackEvent(event, data) {
        if (__DEV__) {
            // eslint-disable-next-line
            console.info('track', data);
        }

        if (Project.mixpanel) {
            if (!event) {
                // eslint-disable-next-line
                console.error("MIXPANEL: Invalid event provided", data);
            }
            mixpanel.track(event, data || {});
        }
    },
    alias(id) {
        if (Project.mixpanel) {
            mixpanel.alias(id);
        }
        if (Project.branch) {
            try {
                branch.setIdentity(id.toString());
            } catch (e) {
                console.log('Error setting identity in branch', e);
            }
        }
        if (Project.braze) {
            appboy.changeUser(id);
        }
    },
    identify(id) {
        if (Project.mixpanel) {
            mixpanel.identify(id);
        }
        if (Project.branch) {
            try {
                branch.setIdentity(id.toString());
            } catch (e) {
                console.log('Error setting identity in branch', e);
            }
        }
        if (Project.braze) {
            appboy.changeUser(id);
        }
    },
    register(email, firstName, lastName, dobDate) {
        if (Project.mixpanel) {
            mixpanel.register({
                'Email': email,
                'First Name': firstName,
                'Last Name': lastName,
                'Date Of Birth': dobDate.format('DD-MM-YYYY'),
            });
        }
    },
    setBrazeCustomAttributes(email, firstName, lastName, dobDate, { city, country }) {
        if (Project.braze) {
            const user = appboy.getUser();
            user.setCountry(country);
            user.setHomeCity(city);
        }
    },
    reset() {
        if (Project.mixpanel) {
            mixpanel.reset();
        }
        if (Project.branch) {
            try {
                branch.logout();
            } catch (e) {
                console.log('Error logging out of branch', e);
            }
        }
    },
    setUserRegistrationProperties(email, firstName, lastName, dobDate) {
        if (Project.mixpanel) {
            mixpanel.people.set({
                '$email': email,
                '$first_name': firstName,
                '$last_name': lastName,
                'Date Of Birth': dobDate.format('DD-MM-YYYY'),
            });
        }
    },
    setUserProperties(properties) {
        if (Project.mixpanel) {
            mixpanel.people.set(properties);
        }
    },
    unsetUserProperties(properties) {
        if (Project.mixpanel) {
            mixpanel.people.unset(properties);
        }
    },
    trackDiscountApplied(coupon, eventData) {
        if (coupon) {
            eventData['Discount Applied'] = coupon.name;
            API.setUserProperties({ 'Discount Applied': coupon.name });
        } else {
            API.unsetUserProperties('Discount Applied');
        }
    },
    log(namespace, ...args) {
        if (Project.logs[namespace]) {
            // eslint-disable-next-line no-console
            console.log.apply(this, [namespace, ...args]);
        }
    },
    partnerstackSignup(id, firstName, lastName, email) {
        return new Promise((resolve) => {
            if (!window.growsumo) return resolve();
            growsumo.data.name = `${firstName} ${lastName}`;
            growsumo.data.email = email;
            growsumo.data.customer_key = id + '';
            growsumo.createSignup(() => resolve());
        });
    },
    logBranchEvent(event, data) {
        if (Project.branch) {
            branch.logEvent(event, data, (err) => {
                if (err) {
                    bugsnagClient.notify(new Error('Failed to log Branch event'), {
                        beforeSend: report => {
                            // this metaData will override the original metaData
                            report.metaData = { error: err }
                        }
                    });
                }
            })
        }
    },

    async authCognitoUser (token = null) {
        let idToken = token ?? API.getCookie('evolveyou_id_token');

        try {
            if (typeof idToken === 'string') {
                let tokenState = await validateCognitoToken(idToken);

                if (tokenState === TOKEN_VALIDATION_STATE.Expired) {
                    idToken = await refreshTokens();
                    tokenState = await validateCognitoToken(idToken);
                }

                return {
                    idToken,
                    tokenState,
                }
            } else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }
};

global.API = API;
export default API;
